import type {ReactElement} from 'react';
import React, {useRef} from 'react';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {ControlledSlider} from '@Components/controlled-slider';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import useLongPress from '@Hooks/useLongPress';
import styles from './slider-text-options.module.scss';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';

interface SliderTextOptionsProps {
  id?: string;
  className?: string;
  data: string[];
  label: string;
  value: string;
  selectedId: string;

  onChange(val: string): void;

  onChangeEnded?(val: string): void;

  isDisabled?: boolean;
}

export function SliderTextOptions({id = '', isDisabled = false, className = '', onChangeEnded = (): void => {}, ...props}: SliderTextOptionsProps): ReactElement<any> | null {
  const incrementContainerRef = useRef<HTMLDivElement | null>(null);
  const decrementContainerRef = useRef<HTMLDivElement>(null);
  const onIncrementDecrementTimer = useRef<number | undefined>(undefined);

  const handleIncrementDecrement = (newIndex: number): void => {
    props.onChange(props.data[newIndex]);

    if (onIncrementDecrementTimer) {
      clearTimeout(onIncrementDecrementTimer.current);
    }

    onIncrementDecrementTimer.current = window.setTimeout(() => {
      onChangeEnded(props.data[newIndex]);
    }, 600);
  };

  const preIncrementCallback = (): void => {
    if (isDisabled || isRightDisabled()) {
      return;
    }
    const currentVal = props.data.indexOf(props.selectedId);
    handleIncrementDecrement(currentVal + 1);
  };

  const preDecrementCallback = (): void => {
    if (isDisabled || isLeftDisabled()) {
      return;
    }
    const currentVal = props.data.indexOf(props.selectedId);
    handleIncrementDecrement(currentVal - 1);
  };

  const incrementLongPress = useLongPress(preIncrementCallback, 75);
  const decrementLongPress = useLongPress(preDecrementCallback, 75);

  const isLeftDisabled = (): boolean => {
    return props.data.indexOf(props.selectedId) === 0;
  };

  const isRightDisabled = (): boolean => {
    return props.data.indexOf(props.selectedId) === props.data.length - 1;
  };

  const getTextInputCounter = (): ReactElement<any> => {
    return (
      <div className={`flex-row-align-center radius-4 ${styles.textInputCounter}`}>
        <div
          {...decrementLongPress}
          ref={decrementContainerRef}
          onClick={(): void => {
            preDecrementCallback();
          }}
          className={`${styles.iconContainer} ${styles.smallControl} flex-center ${isDisabled ? styles.disabledBorderRight : styles.borderRight} ${
            isDisabled || isLeftDisabled() ? styles.disabledControl : ''
          } ${styles.leftControl} `}
        >
          <Icon icon="icon-minus" size={IconSize.SIZE_ICON_16} />
        </div>
        <InputField className={styles.inputContainer} size={InputFieldSize.XSMALL} inputClassName={`${styles.inputClassName} _borderless`} input={props.value} isReadOnly />
        <div
          {...incrementLongPress}
          ref={incrementContainerRef}
          onClick={(): void => {
            preIncrementCallback();
          }}
          className={`${styles.iconContainer} ${styles.smallControl} flex-center ${isDisabled ? styles.disabledBorderLeft : styles.borderLeft}  ${
            isDisabled || isRightDisabled() ? styles.disabledControl : ''
          } ${styles.rightControl}`}
        >
          <Icon icon="icon-plus" size={IconSize.SIZE_ICON_16} />
        </div>
      </div>
    );
  };

  const getValueFromText = (): number => {
    return props.data.indexOf(props.selectedId);
  };

  const onSlide = (value: number): void => {
    props.onChange(props.data[value]);
  };
  const onSlideEnded = (value: number): void => {
    onChangeEnded(props.data[value]);
  };

  return (
    <div key={id} className={`${className} hover-transition-all flex-v-row ${styles.container}`}>
      <div className={`flex-row-justify-between flex-items-center spacing-m-b-3 ${styles.upperContainer}`}>
        <Text val={props.label} size={TextSize.XSMALL} className={`${styles.text}  ${isDisabled ? 'color-extra-light' : 'content-sub-text'} spacing-m-r-5`} />
        {getTextInputCounter()}
      </div>
      <ControlledSlider onSlideEnded={onSlideEnded} onSlide={onSlide} value={getValueFromText()} min={0} max={props.data.length - 1} step={1} isDisabled={isDisabled} />
    </div>
  );
}
